import _ from 'lodash';
import { createContext, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { hashmark, useHash } from "../../../App";
import { allPageTypes } from "../../enums/allPageTypes";
import { gEnums } from "../../enums/globalEnums";
import { currentHelpers } from "../../redirection/current";
import { clickToMethods } from "../reducers/ElementReducer";
import { PageContext } from "./PageContext";
import { PapsContext } from "./PapsContext";
import { CurrentSettingsContext, HomeSettingsContext } from "./SettingsDataContext";
import { AppSettingsContext } from './AppSettingsContext';
import { useAppUserAccess } from './useHelpers/useAppUserAccess';

// Create context
export const NavigationContext = createContext(null);

export const NavigationProvider = ({ children }) => {

  const navigate = useNavigate();

  const homeSettingsContext = useContext(HomeSettingsContext);
  const { paps_state } = useContext(PapsContext) ?? {};
  const { page_state } = useContext(PageContext) ?? {};
  const { currentSettings_state } = useContext(CurrentSettingsContext) ?? {};

  const appSettingsContext = useContext(AppSettingsContext)
  const { appSettings_state, appSettings_handlers } = appSettingsContext ?? {}
  const { appHistory } = appSettings_state ?? {}

  const { aps_viewItems } = _.get(currentSettings_state, 'gvviSettings', {});
  const { aps_page } = _.get(page_state, 'pageSettings', {});

  const appUserAccess = useAppUserAccess();

  const navDeps = useMemo(() => ({
    homeSettingsContext,
    aps_page,
    aps_viewItems,
    appUserAccess,
    paps_state
  }), [homeSettingsContext, aps_page, aps_viewItems, appUserAccess, paps_state]);

  const [navigation_state, setNavState] = useState({
    appSidebarOpen: false,
    showAppBottomSidebar: false,
    showFullPage: null
  });

  // const getKeyFromPath = (path, prop) => {
  //   const parts = _.split(path, "/");
  //   const idx = _.indexOf(parts, prop);
  //   return _.get(parts, idx + 1, null);
  // };

  const push_simple = (navProps) => {
    const np = truePushSimple(navDeps, navProps);
    if (np) {
      if (navProps?.modalMode) {
        navigate(np, { state: { modalMode: navProps.modalMode } });
      } else {
        navigate(np, { state: { modalMode: null } });
      }
    }
  };

  const navigation_fns = {
    handleNav: (navProps) => push_simple(navProps),
    handleNav_item: (clickedItem, itemKey, modifyMode, opts, ddn, ddProps, ddGroupIndex) => {
      push_simple({ clickedItem, itemKey, modifyMode, opts, ddn, ddProps, ddGroupIndex });
    },
    handleNav_path: (path) => navigate(path),
    handleNav_url: (e, url) => {
      if (url && !url.startsWith("https://")) {
        url = "https://" + url;
      }
      e?.preventDefault();
      e?.stopPropagation();
      window.open(url, "_blank");
    },
    handleNav_back: (count = 1) => {
      navigate(-count);
    }
  };

  return (
    <NavigationContext.Provider value={{
      navigation_fns,
      navigation_state,
    }}>
      {children}
    </NavigationContext.Provider>
  );
};

// Custom hook to use the navigation context
export const useNavigation = () => {
  return useContext(NavigationContext);
};


const openAppUrl = (appUrl) => { window.open(appUrl, '_blank') }

const truePushSimple = (xxx, navProps, isAppDeveloper) => {
  const {
    homeSettingsContext,
    aps_page,
    aps_viewItems,
    appUserAccess,
    paps_state
  } = xxx ?? {}

  const {
    clickedItem,
    ddGroupIndex,
    ddn,
    ddProps,
    itemKey,
    modifyMode,
    opts,
  } = navProps
  // item edits and add handled from `dataReducer`     

  let { key: itemView, drillDown, itemKey: itemKey_clicked } = clickedItem ?? {}

  const clickTos = {
    app: false,
    detect: false,
    noClick: false,
    page: false,
    pdf: false,
    url: false,
  }

  if (clickTos.noClick && appUserAccess && (appUserAccess.accessLevel <= gEnums.accessLevels.admin)) { return false }

  const { homeSettings_state } = homeSettingsContext ?? {}
  const { homeSettings } = homeSettings_state ?? {}
  const { global: global_home } = homeSettings ?? {}
  const { appLanding, appShortUrls } = global_home ?? {}
  const { useAppLanding, appLandingUrls } = appLanding ?? {}
  const { useAppShortUrls, appLandingShortUrls } = appShortUrls ?? {}

  const { hash, pathname } = window.location
  const pathNameH = useHash ? hash.replace(hashmark + '/', '/') : pathname

  const { pathName, pathViews, landingView } = paps_state ?? {}

  const _isClient = pathViews?.clients
  const _isEvent = pathViews?.events

  let { clickTo } = opts ?? {}
  let { clickToMethod, page: click_toPage, ignoreClickMethod } = clickTo ?? {}

  if (clickTo) {
    switch (clickToMethod) {
      case clickToMethods.noClick:
        if (!ignoreClickMethod) {
          console.log('NO CLICK')
          return false
        }
      case clickToMethods.openAppUrl:
      case clickToMethods.openAppPdf:
        if (!ignoreClickMethod) {
          openAppUrl(clickTo.value)
          return false
        }
        break;
      case clickToMethods.page:
        itemView = click_toPage
        break;
      case clickToMethods.normal:
        break;
      default:
      // nothing
    }
  }

  const { itemData, directPath } = opts ?? {}
  if (itemData && itemData.as && itemData.as !== itemView) { itemView = itemData.as }

  const _itemKey = itemKey_clicked ? itemKey_clicked : itemKey

  let newPath = ddProps ? pathName : getNewPath(aps_viewItems, aps_page, paps_state, itemView, _itemKey, modifyMode, landingView, pathNameH)

  if (opts && opts.pageExt) { newPath += '/' + opts.pageExt }

  window.localStorage.removeItem('isBack')

  const _host = document.location.host

  switch (newPath) {
    case '/landing':
      newPath = '/'
      break;
    default:
    // nothing
  }

  // IMPORTANT: Push
  if (directPath) {
    currentHelpers.storageItem_set('lastPath', directPath);
    currentHelpers.storageItem_set('routeDepth', Object.keys(directPath).length);
    return directPath
    // navigate(directPath)
  } else {
    if (drillDown && drillDown.useDrillDown && opts) {
      let newDDGroupIndex = ddGroupIndex ? ddGroupIndex + 1 : 2
      return newPath + '/ddg/' + newDDGroupIndex
    } else if (ddn) {
      return currentHelpers.getSearchPath(newPath, ddProps, ddn)
    } else {
      if (pathNameH !== newPath) {
        if (useAppLanding && appLandingUrls && !isAppDeveloper && !_isClient && !_isEvent) {
          appLandingUrls.forEach(appLandingUrl => {
            const { host, hostUrl, baseUrl } = appLandingUrl
            if ((_host === host) && newPath.indexOf(baseUrl) < 0) {
              newPath = hostUrl
            }
          })
        }
        if (useAppShortUrls && appLandingShortUrls && !isAppDeveloper && !_isClient && !_isEvent) {
          const svs = currentHelpers.getQueryParamsObject()
          appLandingShortUrls.forEach(appLandingShortUrl => {
            const { shortUrl, redirectUrl } = appLandingShortUrl
            if ((svs === '/' + shortUrl)) {
              newPath = redirectUrl
            }
          })
        }
        currentHelpers.storageItem_set('lastPath', pathName);
        currentHelpers.storageItem_set('routeDepth', Object.keys(pathViews).length);
        return newPath
      }
    }
  }
}

const getGviDeps = (aps_viewItems, uivi, viewKey, aps_page) => {

  const viewItem_page = aps_page && aps_page.viewItems && aps_page.viewItems[uivi] ? aps_page && aps_page.viewItems && aps_page.viewItems[uivi] : null;
  const viewItem_app = aps_viewItems && aps_viewItems[uivi] && aps_viewItems[uivi] ? aps_viewItems[uivi] : null

  // dataSource 
  const { dataConstraints, dataSource } = viewItem_page ?? {}
  const _dataConstraints = dataConstraints ? dataConstraints : dataSource
  const { dataParents: dataParents_page, useSeasonals, dataLinkType, useSubDataCollection, subDataCollectionName } = _dataConstraints ?? {}
  const { dependencies, documentDependencies, dataParents } = viewItem_app && viewItem_app.dataConnection ? viewItem_app.dataConnection : {}

  if (dependencies || dataParents || dataParents_page) {
    const dpp = dataParents_page ? dataParents_page : dataParents
    return { dependencies, documentDependencies, dataParents: dpp, useSeasonals, dataLinkType, useSubDataCollection, subDataCollectionName }
  } else {
    let deps = null
    switch (uivi) {
      case allPageTypes.home:
        break;

      case allPageTypes.clients:
        if (viewKey) {
          deps = [allPageTypes.clients]
        }
        break;

      case allPageTypes.events:
        deps = [allPageTypes.clients]
        break;

      default:
        // deps = null
        deps = [allPageTypes.clients, allPageTypes.events]
        break;
    }

    return { dependencies: deps, documentDependencies, dataParents: null, useSeasonals, dataLinkType, useSubDataCollection, subDataCollectionName }
  }
}

const getNewPath = (aps_viewItems, aps_page, paps_state, itemView, itemKey, modifyMode, landingView, pathNameH) => {

  const { mainViews, lastRootPath, lastPathName } = paps_state ?? {}

  const pathViews = currentHelpers.getPathViews(pathNameH)

  let basePath = '';
  let newPath = '';

  switch (itemView) {

    case 'home':
      newPath = '/'
      break;

    case 'allClients':
      newPath = '/clients'
      break;

    case 'client':
      newPath = '/clients/' + pathViews['clients']
      break;

    default:

      if (modifyMode) {

        switch (lastPathName) {
          case landingView:
            newPath = lastRootPath
            break;
          default:
            newPath = pathNameH
        }

        switch (modifyMode) {
          case 'add':
            newPath += '/' + modifyMode
            break;
          case 'edit':
            newPath += '/' + modifyMode
            break;
          case 'viewSettings':
            newPath += '/' + modifyMode
            break;
          case 'qrCode':
            newPath += '/qrCode'
            break;
          case 'notifications':
            newPath += '/notifications'
            break;
          default:
          // nothing
        }

      } else {

        const x = getGviDeps(aps_viewItems, itemView, null, aps_page)
        const { dependencies, documentDependencies, dataParents } = x
        const pvKeys = Object.keys(pathViews)

        let _dataParents = dataParents

        switch (itemView) {
          case 'media':
            _dataParents = ['organizations', 'districts', 'sports']
            break;
          default:
          // nothing
        }

        if (dependencies && pathViews) {
          Object.keys(dependencies).forEach(key => {
            const dep = dependencies[key]
            if (pathViews[dep]) {
              basePath += '/' + dep + '/' + pathViews[dep]
            }
          })
        }

        if (documentDependencies) {
          documentDependencies.forEach(dd => {
            if (pvKeys.includes(dd)) {
              basePath += '/' + dd + '/' + pathViews[dd]
            }
          })
        }

        // IMPORTANT: add path for dataParents
        if (_dataParents && pathViews) {
          Object.keys(_dataParents).forEach(key => {
            const dep = _dataParents[key]
            if (pathViews[dep]) {
              basePath += '/' + dep + '/' + pathViews[dep]
            }
          })
        }

        newPath = basePath

        if (itemView) {
          newPath += '/' + itemView
          if (itemKey) {
            newPath += '/' + itemKey
            if (mainViews.includes(itemView)) {
              newPath += '/' + landingView
            }
          }
        }
      }
  }

  return newPath

} 